import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { backendAccessGet } from "../Utils/Authentication"
import { backendAccessPost } from "../Utils/Authentication"

const PopupModal = ({ isOpen, onClose, title = 'Ticket Details', children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white w-[90vw] max-w-4xl max-h-[85vh] rounded-xl shadow-xl overflow-hidden">
        <div className="flex justify-between items-center px-4 py-3 border-b">
          <h3 className="font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="px-2 py-1 rounded hover:bg-gray-200"
          >
            ✕
          </button>
        </div>

        {/* Scrollable content */}
        <div className="p-4 overflow-y-auto overflow-x-hidden max-h-[75vh]">
          {children}
        </div>
      </div>
    </div>
  );
};

const TicketCard = ({
  title,
  status,
  tickets,
  adminUsers,
  statusColors,
  onTicketClick,
  onStatusChange,
  onAssign,
  onArchive,
  onReopen
}) => {
  const isArchived = status === 'archived';

  return (
    <div className={`p-5 rounded-xl shadow ${status === 'open' ? 'bg-blue-50' :
      status === 'processing' ? 'bg-yellow-50' :
        status === 'close' ? 'bg-green-50' :
          'bg-gray-100'
      }`}>
      <h3 className={`pb-2 mb-4 border-b ${statusColors[status]} font-medium`}>
        {title} ({tickets.length})
      </h3>
      {tickets.length === 0 ? (
        <p className="text-gray-500 text-sm">
          {isArchived ? 'No archived tickets' : `No ${status} tickets`}
        </p>
      ) : (
        tickets.map((ticket) => (
          <div
            key={ticket.id}
            // onClick={() => onTicketClick(ticket)}
            className={`border border-gray-200 p-4 mb-4 rounded-lg  hover:bg-${status === 'open' ? 'blue' :
              status === 'processing' ? 'yellow' :
                status === 'close' ? 'green' :
                  'gray'
              }-100 transition`}
          >
            <div className="font-medium text-gray-900">{ticket.referenceID}</div>
            <div className="text-sm text-gray-700 mt-1">{ticket.subject}</div>
            <div className="text-xs text-gray-500 mt-2">{ticket.last_updated}</div>

            {status !== 'open' && (
              <div className="text-xs text-gray-600 mt-1">
                Assigned to: <span className="font-medium">{ticket.assigned_to || 'Unassigned'}</span>
              </div>
            )}

            <div className="flex items-center mt-3 gap-2">
              {status === 'open' && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onStatusChange(ticket.referenceID, 'processing');
                    }}
                    className="px-3 py-1.5 bg-yellow-100 text-black rounded text-xs hover:bg-yellow-200 transition"
                  >
                    Assign
                  </button>
                  <select
                    onChange={(e) => onAssign(ticket.referenceID, e.target.value)}
                    className="ml-2 px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    <option value="">Assign To...</option>
                    {adminUsers.map((admin) => (
                      <option key={admin.id} value={admin.username}>
                        {admin.username} ({admin.email})
                      </option>
                    ))}
                  </select>
                </>
              )}

              {status === 'processing' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusChange(ticket.referenceID, 'close');
                  }}
                  className="px-3 py-1.5 bg-green-100 text-black rounded text-xs hover:bg-green-200 transition"
                >
                  Close
                </button>
              )}

              {status === 'close' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive(ticket.referenceID);
                  }}
                  className="px-3 py-1.5 bg-gray-500 text-white rounded text-xs hover:bg-gray-600 transition"
                >
                  Archive
                </button>
              )}

              {isArchived && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onReopen(ticket.referenceID);
                  }}
                  className="px-3 py-1.5 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition"
                >
                  Reopen
                </button>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTicketClick(ticket);
                }}
                className="ml-auto px-3 py-1.5 bg-black text-white rounded text-xs hover:bg-gray-800 transition"
              >
                {isArchived ? 'View Details' : 'Reply'}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [file, setFile] = useState(null);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newTicketFile, setNewTicketFile] = useState(null);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({
    name: '',
    family: '',
    email: '',
    subject: '',
    description: '',
    source: 'ETD',
  });
  const navigate = useNavigate();

  useEffect(() => {
    const authData = localStorage.getItem('auth');
    const userData = localStorage.getItem('me');

    if (!authData || !userData) {
      navigate('/');
      return;
    }

    try {
      const user = JSON.parse(userData);
      setCurrentUser(user);

      backendAccessGet('/api/tickets/admin/users/').then(res => {
        try {
          setAdminUsers(res);
        } catch (error) {
          console.error('Failed to load admin users:', error);
          // if (error.message.includes('token')) {
          //   navigate('/');
          // }
        }
      })

      backendAccessGet('api/tickets/all-references/').then(res => {
         setLoading(false);
        try {
          setTickets(res);
        } catch (error) {
          console.error('Failed to load admin users:', error);
         
        }
      })
    } catch (e) {
      console.error('Invalid auth data', e);
      navigate('/');
    }
  }, []);




  const handleStatusChange = async (referenceID, newStatus) => {
    let latestTicket = []

    backendAccessGet(`/api/tickets/reference/${referenceID}/`).then(res => {
      try {
        latestTicket = res[0];
        console.log(latestTicket)
         backendAccessPost(
      '/api/tickets/update-status/',
      { id: res[0].id, status: newStatus },
      { 'Content-Type': 'application/json' }
    ).then(res => {
      try {
        setTickets(res);
      } catch (error) {
        console.error('Failed to load admin users:', error);

      }
    })

      } catch (err) {
        alert('Failed to update status');
      }

    })

 
  }




  const handleAssign = async (referenceID, assignedToUsername) => {
    if (!assignedToUsername) return;

    try {
      const thread = await backendAccessGet(`/api/tickets/reference/${referenceID}/`);
      const latestTicket = thread[0];

      await backendAccessPost(
        '/api/tickets/assign/',
        { id: latestTicket.id, assigned_to: assignedToUsername },
        { 'Content-Type': 'application/json' }
      );

      const res = await backendAccessGet('api/tickets/all-references/');
      setTickets(res);
    } catch (err) {
      console.error('Assignment failed:', err);
      alert('Failed to assign ticket');
    }
  };

  const archiveTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to archive this ticket? It will disappear from the Close card.')) return;

    await axios.put('/api/tickets/archive/', {
      referenceID: ticketId
    }).then(res => {
      try {
        setTickets(res)
      } catch (err) {
        alert('Failed to archive ticket');
      }
    }
    )
  };



const loadThread = async (ticket) => {

  backendAccessGet(`/api/tickets/reference/${ticket.referenceID}/`).then(res => {
    try {
      setSelectedThread({ ...ticket, messages: res });
    } catch (err) {
      console.error('Failed to load thread:', err);
      alert('Failed to load ticket details');
    }
  }
  )

};

const submitReply = async () => {
  if (!replyText.trim() || isSubmittingReply) return;

  setIsSubmittingReply(true);

  try {
    const fileData = file ? await readFileAsBase64(file) : null;

    const payload = {
      name: currentUser.username,
      family: '',
      email: currentUser.email,
      subject: `Re: ${selectedThread.subject}`,
      description: replyText,
      parent_referenceID: selectedThread.referenceID,
      status: 'processing',
      source: selectedThread.source || 'ETD',
      file: fileData,
    };

    const res = await backendAccessPost(
      '/api/tickets/create/',
      payload,
      { 'Content-Type': 'application/json' }
    );

    setReplyText('');
    setFile(null);
    setSelectedThread(null);
    setTickets(res);
  } catch (err) {
    console.error('Reply error:', err);
    alert('Failed to send reply: ' + (err.message || 'Unknown error'));
  } finally {
    setIsSubmittingReply(false);
  }
};

const updateNewTicket = (e) => {
  const { name, value } = e.target;
  setNewTicket((prev) => ({ ...prev, [name]: value }));
};

const resetNewTicket = () => {
  setNewTicket({
    name: '',
    family: '',
    email: '',
    subject: '',
    description: '',
    source: 'ETD',
  });
  setNewTicketFile(null);
};

const closeCreateModal = () => {
  if (isCreatingTicket) return;
  resetNewTicket();
  setIsCreateModalOpen(false);
};

const submitNewTicket = async (e) => {
  e.preventDefault();

  if (!newTicket.name.trim() || !newTicket.email.trim() || !newTicket.subject.trim() || !newTicket.description.trim()) {
    alert('Please fill all required fields.');
    return;
  }

  setIsCreatingTicket(true);

  try {
    const fileData = newTicketFile ? await readFileAsBase64(newTicketFile) : '';
    const res = await backendAccessPost(
      '/api/tickets/create/',
      {
        ...newTicket,
        file: fileData,
      },
      { 'Content-Type': 'application/json' }
    );

    setTickets(res);
    resetNewTicket();
    setIsCreateModalOpen(false);
  } catch (err) {
    console.error('Create ticket error:', err);
    alert('Failed to create ticket: ' + (err.message || 'Unknown error'));
  } finally {
    setIsCreatingTicket(false);
  }
};

const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const logout = () => {
  localStorage.removeItem('auth');
  localStorage.removeItem('me');
  navigate('/');
};

if (loading) return (
  <div className="flex items-center justify-center min-h-screen">
    <p className="text-gray-600">Loading...</p>
  </div>
);

const open = tickets.filter(t => t.status === 'open');
const processing = tickets.filter(t => t.status === 'processing');
const close = tickets.filter(t => t.status === 'close');
const archived = tickets.filter(t => t.status === 'archived');

const statusColors = {
  open: 'text-blue-500 border-blue-500',
  processing: 'text-orange-500 border-orange-500',
  close: 'text-green-500 border-green-500',
  archived: 'text-gray-500 border-gray-500'
};

return (
  <div className="bg-white min-h-screen flex flex-col">
    <div className="flex justify-between items-start p-[20px]">
      <div>
        <h1 className="text-xl font-semibold">Support Dashboard</h1>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-sm"
        >
          Add New Ticket
        </button>
      </div>


      <div className="flex  space-x-[10px]">
        <button
          onClick={() => navigate('/archive')}
          className="w-full text-left px-4 py-2 hover:bg-gray-100"
        >
          Archive
        </button>

        <button
          onClick={logout}
          className="w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100"
        >
          Logout
        </button>
      </div>

    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 flex-1 overflow-x-auto p-6">
      <TicketCard
        title="Open"
        status="open"
        tickets={open}
        adminUsers={adminUsers}
        statusColors={statusColors}
        onTicketClick={loadThread}
        onStatusChange={handleStatusChange}
        onAssign={handleAssign}
      />

      <TicketCard
        title="Processing"
        status="processing"
        tickets={processing}
        adminUsers={adminUsers}
        statusColors={statusColors}
        onTicketClick={loadThread}
        onStatusChange={handleStatusChange}
      />

      <TicketCard
        title="Closed"
        status="close"
        tickets={close}
        adminUsers={adminUsers}
        statusColors={statusColors}
        onTicketClick={loadThread}
        onArchive={archiveTicket}
      />
    </div>

    <PopupModal
      isOpen={!!selectedThread}
      onClose={() => setSelectedThread(null)}
      title="Ticket Details"
    >
      {selectedThread && (
        <div className="w-full min-w-0">
          <h3 className="text-xl font-bold mb-4">
            Ticket #{selectedThread.referenceID} – {selectedThread.subject}
          </h3>

          {/* Messages */}
          <div className="space-y-4 mb-6 bg-gray-50 p-3 rounded border max-h-72 overflow-y-auto overflow-x-hidden">
            {[...(selectedThread.messages || [])].reverse().map((msg) => (
              <div key={msg.id} className="p-3 border-b last:border-0 min-w-0">
                <div className="flex flex-col gap-1 text-sm mb-1 sm:flex-row sm:justify-between">
                  <div className="min-w-0">
                    <div className="font-medium">
                      {msg.data.name || msg.data.email}
                    </div>
                    <div className="whitespace-pre-wrap break-words text-gray-700">
                      {msg.data.description}
                    </div>
                  </div>
                  <span className="shrink-0 text-gray-500">
                    {new Date(msg.created_at).toLocaleString()}
                  </span>
                </div>

                {msg.file && (
                  <a
                    href={msg.file}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 text-sm underline"
                  >
                    📎 View attachment
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* Reply */}
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            rows="4"
            wrap="soft"
            className="w-full border rounded p-2 mb-3 whitespace-pre-wrap break-words overflow-x-hidden resize-y"
            placeholder="Type reply..."
          />

          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="mb-3"
          />

          <div className="flex justify-end gap-2">
            <button
              onClick={() => setSelectedThread(null)}
              className="px-4 py-2 bg-gray-300 rounded"
            >
              Cancel
            </button>
            <button
              onClick={submitReply}
              disabled={!replyText.trim() || isSubmittingReply}
              className="px-4 py-2 bg-blue-600 text-white rounded disabled:bg-gray-400"
            >
              {isSubmittingReply ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </PopupModal>

    <PopupModal
      isOpen={isCreateModalOpen}
      onClose={closeCreateModal}
      title="Add New Ticket"
    >
      <form onSubmit={submitNewTicket} className="w-full min-w-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              name="name"
              value={newTicket.name}
              onChange={updateNewTicket}
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Family</label>
            <input
              type="text"
              name="family"
              value={newTicket.family}
              onChange={updateNewTicket}
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              name="email"
              value={newTicket.email}
              onChange={updateNewTicket}
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <input
              type="text"
              name="source"
              value={newTicket.source}
              onChange={updateNewTicket}
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
          <input
            type="text"
            name="subject"
            value={newTicket.subject}
            onChange={updateNewTicket}
            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            required
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
          <textarea
            name="description"
            value={newTicket.description}
            onChange={updateNewTicket}
            rows="5"
            className="w-full border rounded px-3 py-2 whitespace-pre-wrap break-words overflow-x-hidden resize-y focus:ring-2 focus:ring-blue-500 focus:outline-none"
            required
          />
        </div>

        <input
          type="file"
          onChange={(e) => setNewTicketFile(e.target.files?.[0] || null)}
          className="mb-4"
        />

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={closeCreateModal}
            disabled={isCreatingTicket}
            className="px-4 py-2 bg-gray-300 rounded disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isCreatingTicket}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
          >
            {isCreatingTicket ? 'Creating...' : 'Create Ticket'}
          </button>
        </div>
      </form>
    </PopupModal>


  </div>
);
};

export default AdminDashboard;
